import React from 'react';
import { ArrowDownWideNarrow, ArrowUpNarrowWide } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  formatNumberish,
  rankingTitle,
  type LayerRankingBlock,
  type LayerRankingSetting,
} from './layerStatsFormat';
import { FeatureFocusButton, useFocusExternalFeature } from './mapFocus';

const AUTO_FIELD = '__auto__';

/**
 * One layer's features ordered by a numeric attribute, with a map button per
 * row.
 */
export default function RankedFeaturesTable({
  block,
  layerId,
  layerName,
  onChange,
  disabled = false,
}: {
  block: LayerRankingBlock;
  layerId: string;
  layerName: string;
  /** Omitted in read-only views, which hides the controls. */
  onChange?: (next: LayerRankingSetting) => void;
  disabled?: boolean;
}) {
  const focusFeature = useFocusExternalFeature();
  const fieldValue = block.auto ? AUTO_FIELD : block.field;
  const nextOrder = block.order === 'asc' ? 'desc' : 'asc';
  return (
    <Card size="sm">
      <CardHeader className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">
          {rankingTitle(block)}
          <span className="tw:ml-2 tw:text-xs tw:font-normal tw:text-muted-foreground">
            {layerName}
          </span>
        </CardTitle>
        {onChange ? (
          <div className="tw:flex tw:items-center tw:gap-1.5">
            <Select
              value={fieldValue}
              disabled={disabled}
              onValueChange={(next) =>
                onChange({
                  field: next === AUTO_FIELD ? null : String(next),
                  order: null,
                })
              }
            >
              <SelectTrigger
                size="sm"
                className="tw:h-7 tw:w-44 tw:text-xs"
                aria-label="Rank by"
              >
                <SelectValue>
                  {block.auto ? `Auto (${block.fieldAlias})` : block.fieldAlias}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value={AUTO_FIELD}>Auto</SelectItem>
                {block.numericFields.map((field) => (
                  <SelectItem key={field.name} value={field.name}>
                    {field.alias}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              disabled={disabled}
              title={`Show ${nextOrder === 'asc' ? 'lowest' : 'highest'} first`}
              aria-label={`Show ${nextOrder === 'asc' ? 'lowest' : 'highest'} first`}
              onClick={() =>
                onChange({
                  field: block.auto ? null : block.field,
                  order: nextOrder,
                })
              }
            >
              {block.order === 'asc' ? (
                <ArrowUpNarrowWide />
              ) : (
                <ArrowDownWideNarrow />
              )}
            </Button>
          </div>
        ) : null}
      </CardHeader>
      <CardContent>
        {block.rows.length === 0 ? (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            No features in the area have a {block.fieldAlias} value.
          </p>
        ) : (
          <div className="tw:min-w-0 tw:w-full tw:overflow-hidden tw:rounded-md tw:[&_[data-slot=table-container]]:overflow-x-hidden tw:[&_[data-slot=table-container]]:bg-transparent">
            <Table className="tw:table-fixed tw:[&_[data-slot=table-cell]]:truncate">
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-10">#</TableHead>
                  <TableHead>Feature</TableHead>
                  <TableHead className="tw:w-28">{block.fieldAlias}</TableHead>
                  <TableHead className="tw:w-10">
                    <span className="tw:sr-only">Map</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {block.rows.map((row, index) => (
                  <TableRow key={String(row.objectId)}>
                    <TableCell className="tw:tabular-nums tw:text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      {row.label ?? `Feature ${String(row.objectId)}`}
                    </TableCell>
                    <TableCell className="tw:tabular-nums">
                      {formatNumberish(row.value)}
                    </TableCell>
                    <TableCell className="tw:overflow-visible">
                      <FeatureFocusButton
                        disabled={false}
                        onClick={() =>
                          focusFeature({
                            layerId,
                            objectId: row.objectId,
                            title: row.label ?? layerName,
                            lat: row.lat,
                            lon: row.lon,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
